import { FC } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import Breadcrumbs from "@dashboard/components/breadcrumbs/breadcrumbs";
import { UiButton } from "@domun/component-library";
import { DeliveryPageWrapper } from "../../components/delivery-page-layout/delivery-page-layout.styled";
import { useDeliveryHome } from "../../hooks/use-delivery-home.hook";
import { RootState } from "@dashboard/config/redux/store";
import { resolveLogisticStateLabel } from "../../constants/logistic-states.constants";
import { DeliveryDetails } from "../../entities/delivery.entities";
import { DeliveryOrderPickerModal } from "../../components/delivery-order-picker-modal/delivery-order-picker-modal";
import { useDeliveryOrderPicker } from "../../hooks/use-delivery-order-picker.hook";
import { getDeliveryDisplayId } from "../../utils/delivery-detail.utils";

const DeliveryHome: FC = () => {
  const { t } = useTranslation();
  const {
    history,
    originAddress,
    loadHistory,
    goToCreateManual,
    goToSettings,
    goToDetail,
    goToProfile,
    breadcrumbs,
  } = useDeliveryHome();
  const {
    showPicker,
    orders,
    loading: pickerLoading,
    error: pickerError,
    openOrderPicker,
    closeOrderPicker,
    selectOrder,
    retryLoadOrders,
  } = useDeliveryOrderPicker();
  const loading = useSelector((s: RootState) => s.delivery.historyLoading);
  const error = useSelector((s: RootState) => s.delivery.historyError);

  const renderList = (items?: DeliveryDetails[]) => {
    if (!items?.length) {
      return <p>{t("delivery.home.empty")}</p>;
    }
    return items.map((item) => {
      const displayId = getDeliveryDisplayId(item);
      const navigateId = item.orderId;
      return (
        <div
          key={item.id ?? item.orderId}
          className="list-item"
          onClick={() => navigateId && goToDetail(navigateId)}
          onKeyDown={() => {}}
          role="button"
          tabIndex={0}
        >
          <span>
            #{displayId} —{" "}
            {resolveLogisticStateLabel(item.state, item.subState, t)}
          </span>
          <span>{item.customer?.name || item.customer?.address}</span>
        </div>
      );
    });
  };

  return (
    <DeliveryPageWrapper>
      <div className="header">
        <Breadcrumbs items={breadcrumbs} backButton />
      </div>
      <h2>{t("delivery.home.title")}</h2>

      <div className="card">
        <h3>{t("delivery.home.originAddress")}</h3>
        {originAddress ? (
          <p>
            {originAddress.address}, {originAddress.city}
          </p>
        ) : (
          <p>—</p>
        )}
        <button type="button" className="link-button" onClick={goToProfile}>
          {t("delivery.home.editAddress")}
        </button>
      </div>

      <div className="actions">
        <UiButton onClick={goToCreateManual}>
          {t("delivery.home.newManualShipment")}
        </UiButton>
        <UiButton onClick={openOrderPicker}>
          {t("delivery.home.newOrderShipment")}
        </UiButton>
        <UiButton onClick={goToSettings}>{t("delivery.home.settings")}</UiButton>
      </div>

      <DeliveryOrderPickerModal
        show={showPicker}
        orders={orders}
        loading={pickerLoading}
        error={pickerError}
        onClose={closeOrderPicker}
        onSelect={selectOrder}
        onRetry={retryLoadOrders}
      />

      {error && (
        <p className="error-text">
          {error}{" "}
          <button type="button" className="link-button" onClick={loadHistory}>
            {t("delivery.home.retry")}
          </button>
        </p>
      )}

      {loading ? (
        <p>...</p>
      ) : (
        <>
          <div className="card">
            <h3>{t("delivery.home.inProgress")}</h3>
            {renderList(history?.inProgress)}
          </div>
          <div className="card">
            <h3>{t("delivery.home.recent")}</h3>
            {renderList(history?.all)}
          </div>
        </>
      )}
    </DeliveryPageWrapper>
  );
};

export default DeliveryHome;
